import React, { useEffect, useMemo, useState } from "react";
import supabase from "../supabase";
import { formatDeadline } from "../lib/formatDeadline";

export default function MyRequestsTab({ profile, organization }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("All");

  const statusOptions = useMemo(() => {
    const statuses = requests.map((req) => req.status).filter(Boolean);
    return ["All", ...new Set(statuses)];
  }, [requests]);

  const filteredRequests = useMemo(() => {
    if (statusFilter === "All") return requests;
    return requests.filter((req) => req.status === statusFilter);
  }, [requests, statusFilter]);

  useEffect(() => {
    if (!organization?.id) return;

    const fetchRequests = async () => {
      try {
        const { data, error } = await supabase
          .from("Requests")
          .select(
            `
            id,
            title,
            created_at,
            status,
            brief_data,
            projects ( title )
          `
          )
          .eq("company_id", organization.id)
          .order("created_at", { ascending: false });

        if (error) throw error;
        setRequests(data || []);
      } catch (err) {
        console.error("Error fetching requests:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchRequests();
  }, [organization]);

  if (loading) {
    return <div style={{ padding: "40px" }}>Loading requests...</div>;
  }

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this request? This cannot be undone.")) return;

    try {
      const { error } = await supabase
        .from("Requests")
        .delete()
        .eq("id", id)
        .eq("company_id", organization.id);

      if (error) throw error;
      setRequests((prev) => prev.filter((req) => req.id !== id));
    } catch (err) {
      console.error("Error deleting request:", err);
      alert("Could not delete request.");
    }
  };

  // Helper to color-code priorities
  const getPriorityStyle = (priority) => {
    if (priority === "Urgent")
      return { background: "#fee2e2", color: "#991b1b" };
    if (priority === "Low") return { background: "#f3f4f6", color: "#374151" };
    return { background: "#e0f0ee", color: "#00876e" }; // Normal
  };

  // Helper to color-code statuses
  const statusBadge = (status) => {
    const s = (status || "").toLowerCase();
    if (s.includes("publish")) return styles.badgePublished;
    if (s.includes("in progress")) return styles.badgeInProgress;
    if (s.includes("review")) return styles.badgeReview;
    if (s.includes("draft")) return styles.badgeDraft;
    if (s.includes("pending") || s.includes("approval"))
      return styles.badgePending;
    if (s.includes("edit")) return styles.badgeEdits;
    return styles.badgeDefault;
  };

  return (
    <div>
      <div style={styles.headerRow}>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={styles.selectInput}
        >
          {statusOptions.map((status) => (
            <option key={status} value={status}>
              {status === "All" ? "All Statuses" : status}
            </option>
          ))}
        </select>
      </div>

      <div style={styles.card}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Title</th>
              <th style={styles.th}>Project</th>
              <th style={styles.th}>Date Requested</th>
              <th style={styles.th}>Deadline</th>
              <th style={styles.th}>Priority</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}></th>
            </tr>
          </thead>
          <tbody>
            {filteredRequests.map((req) => {
              const deadline = formatDeadline(req.brief_data?.deadline);
              const priority = req.brief_data?.priority || "Normal";

              return (
                <tr key={req.id} style={styles.tr}>
                  <td style={styles.tdStrong}>{req.title}</td>
                  <td style={styles.td}>
                    {req.projects?.title || "Unassigned"}
                  </td>
                  <td style={styles.td}>
                    {new Date(req.created_at).toLocaleDateString()}
                  </td>
                  <td style={styles.td}>{deadline}</td>
                  <td style={styles.td}>
                    <span
                      style={{
                        ...styles.priorityBadge,
                        ...getPriorityStyle(priority),
                      }}
                    >
                      {priority}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <span style={{ ...styles.badgeBase, ...statusBadge(req.status) }}>
                      {req.status}
                    </span>
                  </td>
                  <td style={{ ...styles.td, textAlign: "right" }}>
                    <button
                      type="button"
                      title="Delete request"
                      aria-label="Delete request"
                      onClick={() => handleDelete(req.id)}
                      style={styles.deleteBtn}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = "#fee2e2")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "transparent")
                      }
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              );
            })}
            {filteredRequests.length === 0 && (
              <tr>
                <td colSpan={7} style={styles.emptyState}>
                  No requests found for this organization.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const styles = {
  headerRow: {
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: "16px",
  },
  card: {
    background: "#fff",
    borderRadius: 14,
    border: "1px solid #e5e7eb",
    overflow: "hidden",
  },
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    textAlign: "left",
    fontSize: 12,
    fontWeight: 800,
    color: "#6b7280",
    padding: "14px 16px",
    borderBottom: "1px solid #e5e7eb",
    background: "#fff",
    textTransform: "uppercase",
  },
  tr: { borderBottom: "1px solid #f3f4f6" },
  td: { padding: "16px", fontSize: 14, verticalAlign: "middle" },
  tdStrong: {
    padding: "16px",
    fontSize: 14,
    fontWeight: 800,
    verticalAlign: "middle",
    color: "#1f2937",
  },
  emptyState: { padding: 24, textAlign: "center", color: "#6b7280" },
  selectInput: {
    padding: "8px 12px",
    borderRadius: "6px",
    border: "1px solid #d1d5db",
    fontSize: "14px",
    background: "#fff",
    minWidth: "180px",
    cursor: "pointer",
  },
  deleteBtn: {
    border: "none",
    background: "transparent",
    color: "#ef4444",
    cursor: "pointer",
    fontSize: "16px",
    lineHeight: 1,
    padding: "6px 8px",
    borderRadius: "6px",
  },
  priorityBadge: {
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
  },
  badgeBase: {
    display: "inline-flex",
    alignItems: "center",
    padding: "5px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 900,
    whiteSpace: "nowrap",
  },
  badgePublished: { background: "rgba(16,185,129,0.12)", color: "#065f46" },
  badgeInProgress: { background: "rgba(34,211,238,0.14)", color: "#0e7490" },
  badgeReview: { background: "rgba(59,130,246,0.12)", color: "#1d4ed8" },
  badgeDraft: { background: "rgba(107,114,128,0.12)", color: "#374151" },
  badgePending: { background: "rgba(245,158,11,0.16)", color: "#92400e" },
  badgeEdits: { background: "rgba(244,63,94,0.14)", color: "#9f1239" },
  badgeDefault: { background: "#f3f4f6", color: "#374151" },
};
